import { useState } from 'react';
import QuizSettingsForm, {
  QuizSettingsFormOutput,
} from 'features/quizzes/components/quiz-form/QuizSettingsForm';
import QuizQuestionForm, {
  newQuestionDefaultValues,
  QuizQuestionFormInput,
} from 'features/quizzes/components/quiz-form/QuizQuestionForm';
import { SubmitHandler } from 'react-hook-form';
import type { CreateQuizData } from 'features/quizzes/types';
import { Button, DialogActions as MuiDialogActions } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface QuizFormProps {
  quizData: CreateQuizData;
  onChange: (data: CreateQuizData) => void;
  onDialogClose: () => void;
  onDialogSubmit: (data: CreateQuizData) => void;
  editForm?: boolean;
}

const DialogActions = ({
  onClose,
  canSubmit,
  onSubmit,
  edit,
}: {
  onClose: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  edit?: boolean;
}) => {
  const { t } = useTranslation('modals');

  return (
    <MuiDialogActions>
      <Button type="button" color="grayscale" onClick={onClose}>
        {edit ? t('quizzes.edit_quiz.btn_cancel_label') : t('quizzes.create_quiz.btn_cancel_label')}
      </Button>
      <Button type="button" onClick={onSubmit} disabled={!canSubmit}>
        {edit ? t('quizzes.edit_quiz.btn_ok_label') : t('quizzes.create_quiz.btn_ok_label')}
      </Button>
    </MuiDialogActions>
  );
};

const QuizFormContainer = ({
  quizData,
  onChange,
  onDialogClose,
  onDialogSubmit,
  editForm,
}: QuizFormProps) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Questions start at index 1 in the navigation
  const currentQuestionIndex = currentStep - 1;

  const goToNextStep = () => setCurrentStep((prevState) => prevState + 1);
  const goToPrevStep = () => setCurrentStep((prevState) => Math.max(0, prevState - 1));

  const insertQuestionIntoState = (data: QuizQuestionFormInput) => {
    const updatedQuestions = [...quizData.questions];
    updatedQuestions[currentQuestionIndex] = data;

    return updatedQuestions;
  };

  const saveQuestion = (data: QuizQuestionFormInput) => {
    const updatedQuestions = insertQuestionIntoState(data);

    onChange({ ...quizData, questions: updatedQuestions });
  };

  const handleQuestionNext: SubmitHandler<QuizQuestionFormInput> = (data) => {
    saveQuestion(data);
    goToNextStep();
  };

  const handleQuestionBack: SubmitHandler<QuizQuestionFormInput> = (data) => {
    saveQuestion(data);
    goToPrevStep();
  };

  const handleQuestionAddNew: SubmitHandler<QuizQuestionFormInput> = (data) => {
    const updatedQuestions = insertQuestionIntoState(data);

    updatedQuestions.push(newQuestionDefaultValues);

    onChange({ ...quizData, questions: updatedQuestions });
    goToNextStep();
  };

  const handleQuestionDelete = () => {
    const updatedQuestions = [...quizData.questions];
    updatedQuestions.splice(currentQuestionIndex, 1);

    onChange({ ...quizData, questions: updatedQuestions });

    // If no questions remain, go back to settings
    if (updatedQuestions.length === 0) {
      setCurrentStep(0);
    }
    // If we deleted the last question, go back one step
    if (currentQuestionIndex >= updatedQuestions.length) {
      goToPrevStep();
    }
    //  Otherwise, stay on the current step (which is now display the next question)
  };

  const handleSettingsSubmit: SubmitHandler<QuizSettingsFormOutput> = (data) => {
    const newData: CreateQuizData = {
      ...quizData,
      title: data.title,
      timeLimitSeconds: data.timeLimitSeconds,
      timingMode: data.timingMode,
      passingScorePercentage: data.passingScorePercentage,
      // showCorrectAnswers: data.showCorrectAnswers,
      questions: [newQuestionDefaultValues],
    };

    onChange(newData);
    goToNextStep();
  };

  const handleSettingsNext: SubmitHandler<QuizSettingsFormOutput> = (data) => {
    const updatedData: CreateQuizData = {
      ...quizData,
      title: data.title,
      timeLimitSeconds: data.timeLimitSeconds,
      timingMode: data.timingMode,
      passingScorePercentage: data.passingScorePercentage,
      // showCorrectAnswers: data.showCorrectAnswers,
    };

    onChange(updatedData);
    goToNextStep();
  };

  const handleDialogSubmit: SubmitHandler<QuizQuestionFormInput | QuizSettingsFormOutput> = (
    data
  ) => {
    if ('choices' in data) {
      const updatedQuestions = insertQuestionIntoState(data);

      onDialogSubmit({ ...quizData, questions: updatedQuestions });
    } else {
      const updatedData: CreateQuizData = {
        ...quizData,
        title: data.title,
        timeLimitSeconds: data.timeLimitSeconds,
        timingMode: data.timingMode,
        passingScorePercentage: data.passingScorePercentage,
        // showCorrectAnswers: data.showCorrectAnswers,
      };

      onDialogSubmit(updatedData);
    }
  };

  const isOnSettings = currentStep === 0;

  if (isOnSettings) {
    return (
      <QuizSettingsForm
        defaultValues={{
          title: quizData.title,
          timeLimitSeconds: quizData.timeLimitSeconds,
          timingMode: quizData.timingMode,
          passingScorePercentage:
            quizData.passingScorePercentage != null ? String(quizData.passingScorePercentage) : '',
          // showCorrectAnswers: quizData.showCorrectAnswers,
        }}
        hasQuestions={quizData.questions.length > 0}
        onSubmit={handleSettingsSubmit}
        onNext={handleSettingsNext}
        renderDialogActions={({ isValid, handleSubmit }) => (
          <DialogActions
            edit={editForm}
            canSubmit={isValid && quizData.questions.length > 0}
            onSubmit={handleSubmit(handleDialogSubmit)}
            onClose={onDialogClose}
          />
        )}
      />
    );
  }

  return (
    <QuizQuestionForm
      key={currentStep}
      defaultValues={quizData.questions[currentQuestionIndex]}
      questionNumber={currentStep}
      totalQuestions={quizData.questions.length}
      onAddQuestion={handleQuestionAddNew}
      onNext={handleQuestionNext}
      onBack={handleQuestionBack}
      onDelete={handleQuestionDelete}
      renderDialogActions={({ isValid, handleSubmit }) => (
        <DialogActions
          canSubmit={isValid}
          onSubmit={handleSubmit(handleDialogSubmit)}
          onClose={onDialogClose}
          edit={editForm}
        />
      )}
    />
  );
};

export default QuizFormContainer;
